import { Component, EventEmitter, Input, OnInit, Output, Pipe, PipeTransform } from "@angular/core";
import { Tools } from "@comunicare/comunicare-helper";
import { Answer, ICoding, INPUT_TYPE, IQuestionQuestionnaire, QuestionResponse } from "@comunicare/comunicare-interfaces";
import moment from "moment";
import { SessionService } from "src/app/providers/session.service";

@Pipe({ name: "isNotCompleted" })
export class IsNotCompletedPipe implements PipeTransform {
  transform(required: boolean, value: string, hasValidate: boolean): boolean {
    return hasValidate && required && !Tools.isDefined(value);
  }
}

@Component({
  selector: "app-encode-question",
  templateUrl: "./encode-question.component.html",
  styleUrls: ["./encode-question.component.scss"],
})
export class EncodeQuestionComponent implements OnInit {
  @Input() question: IQuestionQuestionnaire;
  @Input() response: QuestionResponse;
  @Input() questionnaire: any;
  @Input() questionnaireResponse: any;
  @Input() index = 0;
  @Input() hasValidate = false;

  @Output() responseChange = new EventEmitter<QuestionResponse>();
  @Output() inputChange = new EventEmitter<void>();

  public choices: ICoding[];
  public INPUT_TYPE = INPUT_TYPE;
  public joditConfig = {
    useSearch: false,
    spellcheck: true,
    language: this.sessionService.userLang,
    toolbarButtonSize: "small",
    disablePlugins: "file, media",
  };

  constructor(private sessionService: SessionService) {}

  ngOnInit(): void {
    this.setupChoices();
  }

  onMultiChoiceInput(checked: boolean, value: string, type: INPUT_TYPE): void {
    if (Tools.isNotDefined(this.response.answer)) {
      this.response.answer = new Array<Answer>();
    }
    if (checked) {
      const answer = new Answer();
      // find display code
      const coding = this.question.options?.reference ? this.getValueSetChoice(value) : null;
      answer.valueCoding = {
        code: value,
        display: coding?.display ?? "",
        system: null,
      };
      this.response.answer.push(answer);
    } else {
      const index = this.response.answer.findIndex((a) => a.valueCoding.code === value);
      if (index !== -1) {
        this.response.answer.splice(index, 1);
      }
    }
    this.response.inputType = type;
    this.responseChange.emit(this.response);
    this.inputChange.emit();
  }

  setChoiceInput(value: string, type: INPUT_TYPE): void {
    if (Tools.isNotDefined(this.response.answer)) {
      this.response.answer = new Array<Answer>();
    }
    this.response.answer.splice(0); // remove previous value
    const coding = this.question.options?.reference ? this.getValueSetChoice(value) : null;
    const answer = new Answer();
    answer.valueCoding = {
      code: value,
      display: coding?.display ?? "",
      system: null,
    };
    this.response.answer.push(answer);
    this.response.inputType = type;
    this.responseChange.emit(this.response);
    this.inputChange.emit();
  }

  setTimeInput(dateString: string, type: INPUT_TYPE): void {
    if (Tools.isNotDefined(this.response.answer)) {
      this.response.answer = new Array<Answer>();
    }
    if (!Tools.isDefined(dateString)) {
      this.response.answer = [];
    }
    this.response.answer.splice(0); // remove previous value
    const value = type === INPUT_TYPE.DATE ? moment(dateString).format("yyyy/MM/DD") : moment(dateString).format("HH:mm");
    const answer = new Answer();
    answer.valueCoding = {
      code: value,
      display: value ?? "",
      system: null,
    };
    this.response.answer.push(answer);
    this.response.inputType = type;
    this.responseChange.emit(this.response);
    this.inputChange.emit();
  }

  setInput(value: string, withUnits = false, answerId = 0, multiInputs = false): void {
    if (!Tools.isDefined(value)) {
      this.response.answer = [];
    }
    if (Tools.isNotDefined(this.response.answer)) {
      this.response.answer = new Array<Answer>();
    }
    const valueSet = this.question.options?.reference ? this.choices : null;
    const defaultUnit = withUnits && valueSet ? valueSet[0] : null;
    this.response.inputType = this.question.answerDisplay ? this.question.answerDisplay.type : this.question.type;
    // New answer:
    if (!this.response?.answer?.length) {
      // The answer is actually the "unit" part of the number with unit input:
      if (answerId > 0 && withUnits && !multiInputs) {
        // set default value for the number
        const defaultValue = this.question.answerDisplay?.default ? this.question.answerDisplay.default : "0";
        this.response.answer.push(this.createAnswer(defaultValue, defaultValue));
        // set unit
        this.response.answer.push(this.createAnswer(this.getValueSetChoice(value).code, this.getValueSetChoice(value).display));
      }
      // All other cases:
      else {
        if (multiInputs) {
          const iMax = valueSet?.length ? valueSet.length : answerId + 1;
          for (let i = 0; i < iMax; ++i) {
            if (i === answerId) {
              this.response.answer.push(this.createAnswer(value, value));
            } else {
              const defaultValue = this.question.answerDisplay?.default ? this.question.answerDisplay.default : "";
              this.response.answer.push(this.createAnswer(defaultValue, defaultValue));
            }
            if (withUnits) {
              // set unit
              this.response.answer.push(this.createAnswer(valueSet[i].code, valueSet[i].display));
            }
          }
        } else {
          const answer = this.createAnswer(value, value);
          this.response.answer.push(answer);
          // set the default unit when we are setting the "number" part of the number with unit input:
          if (answerId === 0 && withUnits && !multiInputs && defaultUnit) {
            this.response.answer.push(this.createAnswer(defaultUnit.code, defaultUnit.display));
          }
        }
      }
    }
    // Update answer:
    else {
      // The answer is actually the "unit" part of the number with unit input:
      if (answerId > 0 && withUnits && !multiInputs) {
        this.response.answer[answerId] = this.createAnswer(this.getValueSetChoice(value).code, this.getValueSetChoice(value).display);
      }
      // All other cases:
      else {
        if (multiInputs && withUnits) {
          this.response.answer[answerId * 2] = this.createAnswer(value, value);
        } else {
          this.response.answer[answerId] = this.createAnswer(value, value);
        }
        // if the default "unit" wasn't already set (for some mysterious reason):
        if (answerId === 0 && withUnits && !multiInputs && defaultUnit && !this.response.answer[1]) {
          // set the default unit
          this.response.answer.push(this.createAnswer(defaultUnit.code, defaultUnit.display));
        }
      }
    }
    this.responseChange.emit(this.response);
    this.inputChange.emit();
  }

  getCheckedBox(code: string): boolean {
    const res = this.response?.answer;
    if (res && res.length) {
      return res.findIndex((r) => r.valueCoding.code === code) !== -1;
    }
    return false;
  }

  getDateValue(): Date | null {
    const dateString = this.response?.answer?.[0]?.valueCoding.code ?? this.question.answerDisplay?.default;
    if (Tools.isDefined(dateString)) {
      return moment(dateString, "YYYY/MM/DD").toDate();
    }
    return null;
  }

  private setupChoices(): void {
    const ref = this.question.options?.reference;
    if (ref) {
      const contained = this.questionnaire?.contained?.find((c) => c.resourceType === "ValueSet" && c.idSet === ref);
      this.choices = contained?.compose.include[0].concept ?? [];
    }
  }

  /**
   * Find coding display text for this answer
   */
  private getValueSetChoice(code: string): ICoding {
    return this.choices?.find((c) => c.code === code);
  }

  private createAnswer(code: string, display: string): Answer {
    return {
      valueCoding: {
        code,
        display,
        system: "http://comunicare.be",
      },
    };
  }
}
